import React, { useCallback, useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import axios from 'axios'
import DefaultModalContent from 'components/shared/DefaultModal/DefaultModalContent'
import { Button, makeStyles, Switch } from '@material-ui/core'
import DefaultLayout from '../../layouts/DefaultLayout'
import { applyParamsToUrlPath, downloadFile } from '../../utils'
import { OrganizationDTO } from '../../dtos/OrganizationDTO'
import { OrganizationFilters } from './Filters'
import TableWrapper from '../../layouts/TableWrapper'
import useCredentials from '../../hooks/useCredentials'
import { usePagination } from '../../hooks/usePagination'
import { ActionsCell } from '../../components/ActionsCell'

const useStyles = makeStyles((theme) => ({
  title: {
    marginTop: '48px',
    textAlign: 'center',
    fontSize: '32px',
    fontWeight: 600,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',

    '& p': {
      textAlign: 'center',
      color: theme.palette.neutrals[600],
    },
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',

    '&>button': {
      width: '100%',
      textTransform: 'uppercase',
    },
  },
}))

const Organizations: React.FC = () => {
  const classes = useStyles()
  const { credentials, hasEditPermission } = useCredentials()
  const history = useHistory()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [rows, setRows] = useState<Array<OrganizationDTO>>([])
  const { totalPageCount, setTotalPageCount, currentPageNumber, setPageNumber, filters, applyFilters } = usePagination()

  const [whitelabelModalState, setWhitelabelModalState] =
    useState<{
      white_label: boolean
      id: string
    } | null>(null)

  const loadOrganizations = useCallback(async (): Promise<void> => {
    try {
      setError('')
      const res = await axios.get(
        applyParamsToUrlPath({ page: currentPageNumber, ...filters }, '/api/panel/organizations'),
        {
          headers: credentials,
        }
      )
      setRows(res.data.organizations)
      setTotalPageCount(res.data.meta.total_pages)
    } catch (e) {
      setError(e?.message)
    } finally {
      setLoading(false)
    }
  }, [credentials, currentPageNumber, filters, setTotalPageCount])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  const deleteOrg = async (id: string) => {
    if (confirm('Do you want to delete?')) {
      try {
        await axios.delete(`/api/panel/organizations/${id}`, {
          headers: credentials,
        })
        loadOrganizations()
      } catch (e) {
        console.error(e.message)
      }
    }
  }

  const downloadOrganizations = async () => {
    try {
      const res = await axios.get('/api/panel/organizations/export_csv', {
        headers: credentials,
      })
      downloadFile(res.data, 'organizations')
    } catch (e) {
      setError(e?.message)
      console.error(e.message)
    }
  }

  const onWhiteLabelChange = async (id: string) => {
    try {
      await axios.put(`/api/panel/organizations/${id}/toggle_white_label`, {}, { headers: credentials })
      loadOrganizations()
    } catch (e) {
      console.error(e.message)
    }
  }

  return (
    <DefaultLayout>
      <TableWrapper
        title='EXEC Portals'
        loading={loading}
        isError={!!error}
        errorMessage={error}
        isEmpty={!rows?.length}
        pagination={{
          totalPageCount,
          currentPage: currentPageNumber,
          onChangePage: setPageNumber,
        }}
        actions={
          hasEditPermission && (
            <div className='mb-4 is-flex is-justify-content-flex-end'>
              <button onClick={downloadOrganizations} className='button mr-2' type='button'>
                Download CSV
              </button>
              <Link to='/panel/organizations/new' className='button is-link'>
                New
              </Link>
            </div>
          )
        }
      >
        <table className='table is-fullwidth'>
          <thead>
            <tr>
              <OrganizationFilters applyFilters={applyFilters} />
            </tr>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Subdomain</th>
              <th>Website</th>
              <th>White-Label</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className='is-clickable'
                onClick={(e) => {
                  if ((e.target as HTMLInputElement)?.dataset?.switch) {
                    return
                  }
                  history.push(`/panel/organizations/${row.id}`)
                }}
              >
                <th>{row.id}</th>
                <td>{row.name}</td>
                <td>{row.subdomain}</td>
                <td>{row.website}</td>
                <td>
                  <Switch
                    inputProps={{
                      'data-switch': row.id,
                    }}
                    checked={row.white_label}
                    onChange={() => {
                      setWhitelabelModalState({ white_label: row.white_label!, id: row.id! })
                    }}
                  />
                </td>
                <td>
                  <ActionsCell>
                    <Link to={`/panel/organizations/${row.id}`} className='button is-text mr-2'>
                      Show
                    </Link>
                    {hasEditPermission && (
                      <Link to={`/panel/organizations/${row.id}/edit/details`} className='button is-text mr-2'>
                        Edit
                      </Link>
                    )}
                    {hasEditPermission && (
                      <button type='button' className='button is-danger' onClick={() => deleteOrg(row.id)}>
                        Delete
                      </button>
                    )}
                  </ActionsCell>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrapper>
      <DefaultModalContent
        isModalOpen={!!whitelabelModalState}
        toggleModal={() => setWhitelabelModalState(null)}
        header={
          <h4 className={classes.title}>
            {whitelabelModalState?.white_label ? 'Deactivate White Label?' : 'Activate White Label?'}
          </h4>
        }
        content={
          <div className={classes.content}>
            <p>
              {whitelabelModalState?.white_label
                ? 'Are you sure you want to deactivate the White Label feature?'
                : 'Are you sure you want to activate the White Label feature?'}
            </p>
            <p />
            <p>
              {whitelabelModalState?.white_label
                ? 'If you proceed, you will lose access to all White Label customization options and related features.'
                : ''}
            </p>
          </div>
        }
        actions={
          <div className={classes.actions}>
            <Button variant='outlined' onClick={() => setWhitelabelModalState(null)}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={() => {
                onWhiteLabelChange(whitelabelModalState?.id || '')
                setWhitelabelModalState(null)
              }}
            >
              {whitelabelModalState?.white_label ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        }
      />
    </DefaultLayout>
  )
}

export default Organizations
